import { MailOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AuthCard from '../components/AuthCard';
import api from '../config/api';
import { baseLanguage } from '../i18n/locale';

const { Text, Paragraph } = Typography;

/**
 * Elfelejtett jelszó, 1. lépés (#109): az e-mail cím bekérése.
 *
 * A kiszolgáló ismert és ismeretlen címre ugyanazt válaszolja, és ez az oldal
 * sem tesz különbséget: siker után a semleges „ha a cím szerepel…" szöveg
 * jön, az űrlap eltűnik. A kérés a panel nyelvét is viszi, hogy a levél a
 * szervező nyelvén menjen ki (szerb panel → magyar levél, mint a felület).
 */
const ForgotPassword: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async ({ email }: { email: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email, lang: baseLanguage(i18n.language) });
      setSent(true);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 429 ? t('forgotPassword.tooMany') : t('forgotPassword.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard eyebrow={t('forgotPassword.title')}>
      {sent ? (
        <Alert
          type="success"
          showIcon
          message={t('forgotPassword.sentTitle')}
          description={t('forgotPassword.sentBody')}
        />
      ) : (
        <>
          <Paragraph style={{ margin: 0, color: 'var(--ink-soft)' }}>
            {t('forgotPassword.intro')}
          </Paragraph>
          {error && <Alert type="error" showIcon message={error} />}
          <Form
            name="forgot-password"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={t('auth.email')}
              rules={[
                { required: true, message: t('forgotPassword.emailRequired') },
                { type: 'email', message: t('forgotPassword.emailInvalid') },
              ]}
            >
              <Input
                type="email"
                autoComplete="email"
                prefix={<MailOutlined style={{ color: 'var(--ink-faded)' }} />}
                placeholder={t('auth.email')}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={submitting} block>
                {t('forgotPassword.submit')}
              </Button>
            </Form.Item>
          </Form>
        </>
      )}

      <div style={{ textAlign: 'center' }}>
        <Text style={{ color: 'var(--ink-soft)' }}>
          <Link to="/login" style={{ color: 'var(--vermilion)', fontWeight: 500 }}>
            {t('forgotPassword.backToLogin')}
          </Link>
        </Text>
      </div>
    </AuthCard>
  );
};

export default ForgotPassword;
